import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { api, downloadFile } from "../utils/api";
import { formatLapTime, formatSetupName } from "../utils/formatters";

interface RunBrief {
  runId: string;
  trackWeekId: string | null;
  driverId: string | null;
  driverName: string;
  bestLapTime: number | null;
  top10Avg: number | null;
  eventType: string | null;
  runStatus: string | null;
  sessionDate: string | null;
  dataQualityScore: number | null;
  hasSto: boolean;
}

interface SetupRow {
  key: string;
  setupName: string;
  carName: string | null;
  displayCar: string;
  trackName: string;
  seriesName: string | null;
  runCount: number;
  fast: RunBrief;
  variants: RunBrief[];
}

interface SetupLibraryResponse {
  setups: SetupRow[];
  filters: {
    cars: string[];
    tracks: string[];
    series: string[];
    drivers: { id: string; name: string }[];
  };
  totalSetups: number;
}

const statusClass = (s: string | null): string =>
  s === "race_ready" ? "pill--success" : s === "needs_review" ? "pill--warning" : "";

export default function SetupLibrary() {
  const { teamId } = useParams<{ teamId: string }>();
  const [data, setData] = useState<SetupLibraryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [car, setCar] = useState("");
  const [track, setTrack] = useState("");
  const [series, setSeries] = useState("");
  const [driver, setDriver] = useState("");
  const [downloading, setDownloading] = useState<string | null>(null);
  const [dlError, setDlError] = useState<string | null>(null);

  const handleDownloadSto = async (runId: string, setupName: string) => {
    setDownloading(runId);
    setDlError(null);
    try {
      await downloadFile(`/api/runs/${runId}/setup-file`, `${setupName || "setup"}.sto`);
    } catch (e) {
      setDlError(e instanceof Error ? e.message : "Failed to download setup file");
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api<SetupLibraryResponse>(`/api/comparison/setups?team_id=${teamId}`)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e?.message || "Failed to load setups"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const visible = useMemo(() => {
    if (!data) return [];
    return data.setups.filter(
      (s) =>
        (!car || s.carName === car) &&
        (!track || s.trackName === track) &&
        (!series || s.seriesName === series) &&
        // driver may have run the setup as a non-fastest variant (SF3)
        (!driver || s.fast.driverId === driver || s.variants.some((v) => v.driverId === driver)),
    );
  }, [data, car, track, series, driver]);

  return (
    <div className="page page--wide fade-in">
      <Link to={`/teams/${teamId}`} className="back-link">
        &larr; Back to team
      </Link>

      <header className="editorial-header">
        <div className="editorial-meta">
          <div className="editorial-meta-left">
            <span className="eyebrow">Captured from team telemetry</span>
          </div>
          {data && (
            <div className="editorial-meta-right">
              <span className="pill">{data.totalSetups} setups</span>
            </div>
          )}
        </div>
        <h1 className="editorial-title">Setup Library</h1>
        <p className="editorial-dek">
          Every setup your team has run, fastest first. Find the quick one for a car and track, then compare it to yours.
        </p>
      </header>

      {dlError && (
        <div className="card" style={st.dlError} role="alert">
          {dlError}
        </div>
      )}

      {data && data.setups.length > 0 && (
        <div style={st.filters}>
          <Select label="Car" value={car} onChange={setCar} options={data.filters.cars} />
          <Select label="Track" value={track} onChange={setTrack} options={data.filters.tracks} />
          <Select label="Series" value={series} onChange={setSeries} options={data.filters.series} />
          <Select
            label="Driver"
            value={driver}
            onChange={setDriver}
            options={data.filters.drivers.map((d) => ({ value: d.id, label: d.name }))}
          />
          {(car || track || series || driver) && (
            <button
              className="btn btn-ghost"
              style={{ alignSelf: "flex-end" }}
              onClick={() => { setCar(""); setTrack(""); setSeries(""); setDriver(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading setups</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-title">Could not load setups</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : visible.length === 0 ? (
        <div className="empty-state" style={{ minHeight: "16rem" }}>
          <div className="empty-state-title">No setups yet</div>
          <div className="empty-state-text">
            {data && data.totalSetups > 0
              ? "No setups match these filters."
              : "Upload runs (the desktop agent does this automatically) and your team's setups will appear here."}
          </div>
        </div>
      ) : (
        <div style={st.list}>
          {visible.map((s) => (
            <div key={s.key} className="card" style={st.row}>
              <div style={st.rowMain}>
                <Link to={`/runs/${s.fast.runId}`} style={st.setupName}>
                  {formatSetupName(s.setupName)}
                </Link>
                <div style={st.sub}>
                  {s.displayCar} · {s.trackName}
                  {s.runCount > 1 && <span style={st.variantBadge}>×{s.runCount} runs</span>}
                </div>
              </div>
              <div style={st.rowMeta}>
                <span className="mono" style={st.lap}>{formatLapTime(s.fast.bestLapTime)}</span>
                <span style={st.driver}>{s.fast.driverName}</span>
                {s.fast.runStatus && (
                  <span className={`pill ${statusClass(s.fast.runStatus)}`}>
                    {s.fast.runStatus.replace(/_/g, " ")}
                  </span>
                )}
                {s.fast.hasSto && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={st.compareBtn}
                    onClick={() => handleDownloadSto(s.fast.runId, s.setupName)}
                    disabled={downloading === s.fast.runId}
                    title="Download the original .sto to drop into your iRacing setups folder"
                  >
                    {downloading === s.fast.runId ? "Downloading…" : "Download .sto"}
                  </button>
                )}
                <Link
                  to={`/teams/${teamId}/setups/compare?run_a=${s.fast.runId}${s.fast.trackWeekId ? `&track_week_id=${s.fast.trackWeekId}` : ""}`}
                  className="btn btn-secondary"
                  style={st.compareBtn}
                >
                  Compare
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: (string | { value: string; label: string })[];
}) {
  return (
    <label style={st.selectWrap}>
      <span style={st.selectLabel}>{label}</span>
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)} style={st.select}>
        <option value="">All</option>
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.value;
          const l = typeof o === "string" ? o : o.label;
          return (
            <option key={v} value={v}>
              {l}
            </option>
          );
        })}
      </select>
    </label>
  );
}

const st: Record<string, CSSProperties> = {
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.35rem",
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.82rem",
    fontWeight: 600,
    textDecoration: "none",
    marginBottom: "1rem",
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.85rem",
    marginBottom: "1.25rem",
  },
  selectWrap: { display: "flex", flexDirection: "column", gap: "0.3rem" },
  selectLabel: { fontSize: "0.8125rem", color: "var(--text-muted)", fontWeight: 600 },
  select: { minWidth: "10rem" },
  list: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
    flexWrap: "wrap",
    padding: "0.85rem 1.1rem",
  },
  rowMain: { minWidth: 0 },
  setupName: {
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: "1rem",
    color: "var(--text-strong)",
    textDecoration: "none",
  },
  sub: { fontSize: "0.82rem", color: "var(--text-secondary)", marginTop: "0.2rem", display: "flex", alignItems: "center", gap: "0.5rem" },
  variantBadge: {
    fontSize: "0.8125rem",
    color: "var(--text-muted)",
    border: "1px solid var(--rule)",
    borderRadius: "var(--r-pill)",
    padding: "0.05rem 0.45rem",
  },
  dlError: {
    padding: "0.7rem 1rem",
    marginBottom: "1rem",
    border: "1px solid var(--danger, #b3261e)",
    color: "var(--danger, #b3261e)",
    fontSize: "0.85rem",
  },
  rowMeta: { display: "flex", alignItems: "center", gap: "0.85rem", flexWrap: "wrap" },
  lap: { fontSize: "1rem", fontWeight: 700, color: "var(--live)" },
  driver: { fontSize: "0.82rem", color: "var(--text-secondary)" },
  compareBtn: { padding: "0.35rem 0.8rem" },
};
